import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorage, LocalStorageService } from 'ngx-webstorage';
import { HttpService } from 'src/app/shared/services/httpService.service';
import Swal from 'sweetalert2';
import { InactivityService } from './inactivity.service';
import { environment } from 'src/environments/environment.dev';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { AuthCleanupService } from "src/app/shared/services/auth-clean-up.service";
import { EchoService } from 'src/app/shared/services/echo.service';
import { SearchHistoryService } from 'src/app/shared/services/search-history.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { StateService } from 'src/app/shared/services/state.service';
import { SentryUserService } from 'src/app/shared/services/sentry-user.service';
import { AuthStateService } from 'src/app/shared/services/auth-state.service';
import { StorageIntegrityService } from 'src/app/shared/services/storage-integrity.service';
import { TokenService } from 'src/app/shared/services/token.service';

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private inactivityService: InactivityService;
  private stateService: StateService;

  @LocalStorage()
  public userDetails;
  @LocalStorage()
  private currentCompany;
  isRefreshTokenDialogAlreadyDisplayed = false;
  isExpiredTokenDialogAlreadyDisplayed = false;
  isCustomerLogin = false;
  mingleToken:any;
  unityToken:any;
  unityUserID:any;
  mingleUserID:any;
  public isRefreshingToken = false;
  public company = null;
  private isLoggedIn = false; 
  public buyflow: boolean = false;
  userData: any;
  constructor(
    private http: HttpClient,
    public router: Router,
    private localStorage: LocalStorageService,
    private injector: Injector,
    private ngZone: NgZone,
    private httpService: HttpService,
    private echoService: EchoService,
    private authCleanupService: AuthCleanupService,
    private dbService: NgxIndexedDBService,
    private searchHistoryService: SearchHistoryService,
    private commonService: CommonService,
    private sentryUserService: SentryUserService,
    private authStateService: AuthStateService,
    private storageIntegrityService: StorageIntegrityService,
    private tokenService: TokenService
  ) {
    this.inactivityService = this.injector.get(InactivityService);
    this.stateService = this.injector.get(StateService);
  }

  private getStoredUserDetails(companyName: string): any | null {
    const raw = localStorage.getItem(`tibss.${companyName}-userDetails`);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private getStoredCompanies(): string[] {
    const appsRaw = localStorage.getItem('Applications');
    const apps = appsRaw ? JSON.parse(appsRaw) : [];
    const companies = apps.map((app: any) => app?.name).filter(Boolean);
    const currentCompany = localStorage.getItem('currentCompany');

    if (currentCompany && !companies.includes(currentCompany)) {
      companies.push(currentCompany);
    }

    return companies;
  }

  private purgeStoredTokens(companies: string[] = this.getStoredCompanies()): void {
    companies.forEach(companyName => {
      localStorage.removeItem(`tibss.${companyName}-Token`);
      localStorage.removeItem(`tibss.${companyName}-Refreshtoken`);
      localStorage.removeItem(`tibss.${companyName}-RefreshTokenExpiry`);
    });
  }

  async initializeAuthFromStorage(): Promise<boolean> {
    this.purgeStoredTokens();

    const company = localStorage.getItem('currentCompany');
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const isCustomerLogin = localStorage.getItem('isCustomerLogin') === 'true';
    if (!company || !isLoggedIn || !isCustomerLogin) {
      return false;
    }

    const user = this.getStoredUserDetails(company);
    if (!user) {
      return false;
    }

    this.authStateService.setAuthState({
      user,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      currentCompany: company,
      isCustomerLogin: true,
      error: null
    });

    try {
      const tokenResponse = await this.tokenService.refreshToken(company);
      const token = tokenResponse?.Token || this.tokenService.getToken(company);
      if (!token) {
        throw new Error(`Token refresh did not return an access token for "${company}"`);
      }

      const tokenData = this.authStateService.getCompanyTokenData(company);
      this.authStateService.setAuthState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        currentCompany: company,
        isCustomerLogin: true,
        error: null,
        lastActivity: new Date(),
        sessionExpiry: tokenData?.expiresAt || null
      });
      this.setLoginStatus(true);
      return true;
    } catch {
      this.authStateService.setAuthState({
        token: null,
        isAuthenticated: false,
        isLoading: false
      });
      return false;
    }
  }

  // ─── processLoginResponse ─────────────────────────────────────────────────
  /**
   * Centralised post-login processing.
   * Stores user data for every company in the response,
   * updates AuthStateService, and saves a storage-integrity snapshot.
   *
   * Compatible with both formats:
   *   • Old: { data: { unity: {...}, mingle: {...} } }           (no companies array)
   *   • New: { companies: ['unity','mingle'], data: { ... } }    (dynamic companies)
   */
  async processLoginResponse(response: any): Promise<void> {
    const data = response?.data;
    if (!data) throw new Error('Invalid login response: missing data');

    const companies: string[] = response.companies
      ? response.companies
      : Object.keys(data);

    console.log('[processLoginResponse] companies:', companies);
    companies.forEach(c => {
      const co = data[c];
      console.log(`[processLoginResponse] ${c}:`, {
        Status: co?.Status,
        hasToken: !!co?.Token,
        hasData: Array.isArray(co?.data) && co.data.length > 0,
        host: co?.host
      });
    });

    const successfulCompanies = companies.filter(companyName => {
      const company = data[companyName];
      return company?.Status && company?.Token && Array.isArray(company?.data) && company.data.length > 0;
    });

    if (!successfulCompanies.length) {
      throw new Error(
        `No valid company found in login response. ` +
        `Checked: [${companies.join(', ')}]. ` +
        `Each requires Status=true, Token, and data[]. ` +
        `Got: ${JSON.stringify(companies.map(c => ({ name: c, Status: data[c]?.Status, hasToken: !!data[c]?.Token, hasData: Array.isArray(data[c]?.data) })))}`
      );
    }

    for (const companyName of successfulCompanies) {
      const company = data[companyName];
      const user = company.data?.[0];

      this.setUser(companyName, user);

      // Compute token expiry
      const expiresAt = user.access_token_expired_at
        ? new Date(user.access_token_expired_at)
        : new Date(Date.now() + (environment.JWT_EXPIRATION_TIME_IN_MINUTES || 60) * 60 * 1000);

      this.authStateService.updateCompanyToken(companyName, {
        token:        company.Token,
        expiresAt,
        userId:       user.userID
      });
    }

    this.purgeStoredTokens(successfulCompanies);

    // Determine active company (prefer previously selected one)
    const savedCompany  = localStorage.getItem('currentCompany');
    const activeCompany = savedCompany && successfulCompanies.includes(savedCompany)
      ? savedCompany
      : successfulCompanies[0];

    const activeCompanyData = data[activeCompany];
    const activeUser        = activeCompanyData?.data?.[0];
    if (!activeUser) throw new Error('No valid active company found in login response');

    // Set global localStorage flags
    const activeBaseUrl = activeCompanyData.host
      ? `${activeCompanyData.host}/`
      : (localStorage.getItem('BaseUrl') || environment.BASE_API);

    localStorage.setItem('currentCompany',  activeCompany);
    localStorage.setItem('BaseUrl',         activeBaseUrl);
    localStorage.setItem('isLoggedIn',      JSON.stringify(true));
    localStorage.setItem('isCustomerLogin', JSON.stringify(true));

    console.log('[processLoginResponse] BaseUrl set to:', activeBaseUrl);

    // Build Applications array
    const applications = companies
      .filter(c => successfulCompanies.includes(c))
      .map(c => ({
        name:    c,
        displayName: data[c].companyId || c,
        BaseUrl: data[c].host ? `${data[c].host}/` : activeBaseUrl,
        icon:    data[c].icon || null
      }));
    localStorage.setItem('Applications', JSON.stringify(applications));

    // Set base user (global fallback)
    this.setBaseUser(activeUser);

    // Update central auth state
    this.authStateService.setAuthState({
      user:            activeUser,
      token:           activeCompanyData.Token,
      isAuthenticated: true,
      isLoading:       false,
      currentCompany:  activeCompany,
      isCustomerLogin: true,
      error:           null,
      lastActivity:    new Date(),
      sessionExpiry:   new Date(Date.now() +
        (environment.JWT_EXPIRATION_TIME_IN_MINUTES || 60) * 60 * 1000)
    });

    // Save storage-integrity snapshot AFTER all state is written
    await this.storageIntegrityService.saveState();
  }

  async switchCompany(company: { name: string; BaseUrl?: string }): Promise<void> {
    const companyName = company?.name;
    if (!companyName) {
      throw new Error('Cannot switch company: missing company name.');
    }

    const baseUrl = company.BaseUrl || '';
    const userDetailsRaw = localStorage.getItem(`tibss.${companyName}-userDetails`);
    const userDetails = this.getStoredUserDetails(companyName);

    localStorage.setItem('BaseUrl', baseUrl);
    localStorage.setItem('currentCompany', companyName);

    if (userDetailsRaw) {
      localStorage.setItem('tibss.userDetails', userDetailsRaw);
    } else {
      localStorage.removeItem('tibss.userDetails');
    }

    if (userDetails) {
      this.setBaseUser(userDetails);
    }

    this.company = companyName;

    let token = this.authStateService.getCompanyTokenData(companyName)?.token || null;
    if (!token) {
      this.authStateService.setAuthState({
        user: userDetails || this.authStateService.user,
        currentCompany: companyName
      });

      try {
        await this.tokenService.refreshToken(companyName);
        token = this.tokenService.getToken(companyName);
      } catch (error) {
        console.warn(`[AuthService] Token refresh during company switch failed for "${companyName}"`, error);
      }
    }

    this.authStateService.setAuthState({
      currentCompany: companyName,
      token,
      user: userDetails || this.authStateService.user,
      isAuthenticated: !!token,
      isCustomerLogin: localStorage.getItem('isCustomerLogin') === 'true',
      error: null,
      lastActivity: new Date(),
      sessionExpiry: this.authStateService.getCompanyTokenData(companyName)?.expiresAt || null
    });

    await this.storageIntegrityService.saveState();
  }
  getUser() {
    this.userData = this.userDetails;
    const user ={
      userId: this.userData?.userID,
      name: this.userData?.name,
      userName: this.userData?.userGroup?.[0]?.Code ?? this.userData?.userName ?? ''
    }
    this.sentryUserService.setUserContext(user?.userId, user?.name, user?.userName)
  }
  getUserId(): number {
    return this.userData?.userID || 0;
  }
  setLoginStatus(status: boolean): void {
    this.isLoggedIn = status;
    localStorage.setItem('isLoggedIn', JSON.stringify(status));
  }
  
  isUserLoggedIn(): boolean {
    if (this.isLoggedIn) return true;
  
    // Check persistent status in localStorage if in-memory flag is not set
    const persistedStatus = localStorage.getItem('isLoggedIn');
    this.isLoggedIn = persistedStatus ? JSON.parse(persistedStatus) : false;
    return this.isLoggedIn;
  }

  async login(data) {
    try {
      const response: any = await this.http
        .post(environment.CAS_BASE_RUL + "api/CAS/login", data)
        .toPromise();
      if (response?.status) {
        localStorage.setItem("isCustomerLogin", JSON.stringify(true));
        this.getInactivityService().start();
      }
      return response;
    } catch (err) {
      this.isCustomerLogin = false;
      throw err;
    }
  }
  private userName:string;
  getUserName():string{
     this.userData = this.userDetails;
     this.userName = this.userData?.userName;
     return this.userName;
  }
  async changePassword(data): Promise<boolean> {
    try {
      const payload = {
        "company_group_id": 1,
        "user_name": this.getUserName(),
        // "old_password": data?.oldPassword,
        "new_password": data?.newPassword
      };
      
      const response: any = await this.http
        .post(environment.CAS_BASE_RUL + "companies/user/change/password", payload)
        .toPromise();
  
      if (response?.Status === true) {
        this.logout();
        return true;
      } else {
        return false;
      }
  
    } catch (error) {
      console.error("Error while changing password:", error);
      return false;
    }
  }
  

  private getInactivityService(): InactivityService {
    if (!this.inactivityService && this.isCustomerLogin) {
      this.inactivityService = this.injector.get(InactivityService);
    }
    return this.inactivityService;
  }

  getCompanyName() {
    if (!this.company) {
      this.company = localStorage.getItem("currentCompany");
    }
    return this.company;
  }
  getBuyFlowHost(companyName: string) {
    if (!this.buyflow) {
      this.buyflow = environment[`${companyName}_BUYFLOW`];
    }
    return this.buyflow;
  }

  generateTokenByRefereshToken() {
    const currentCompany = localStorage.getItem("currentCompany") || "unity";
    return this.tokenService.refreshToken(currentCompany);
  }

  // "userName": "test_username12",
  //     "companyId": "unity",
  //     "userID": 8938,
  async generateRefreshTokenByRefereshToken() {
    if (this.isRefreshingToken) {
      return; // Skip if already refreshing
    }
    this.isRefreshingToken = true;
    try {
      const currentCompany = localStorage.getItem("currentCompany") || "unity";
      await this.tokenService.refreshToken(currentCompany);
    } catch (err) {
      console.log("err", err);
      this.logout();
      throw Error(err?.error?.message);
    } finally {
      this.isRefreshingToken = false; // Reset the flag after completion
    }
  }

  setToken(companyId, token) {
    const existing = this.authStateService.getCompanyTokenData(companyId);
    this.authStateService.updateCompanyToken(companyId, {
      ...existing,
      token
    });
  }

  get getToken() {
    return this.localStorage.retrieve("token");
  }

  setRefreshToken(companyId, refreshToken) {
    const existing = this.authStateService.getCompanyTokenData(companyId);
    this.authStateService.updateCompanyToken(companyId, {
      ...existing,
      ...(refreshToken ? { refreshToken } : {})
    });
    this.isRefreshTokenDialogAlreadyDisplayed = false;
    this.isExpiredTokenDialogAlreadyDisplayed = false;
  }

  setUser(companyId, data) {
    this.localStorage.store(companyId + "-userDetails", data);
  }

  setBaseUser(data) {
    this.localStorage.store("userDetails", data);
  }

  async logout() {
    // 1. Fire signout API immediately (keepalive — survives page navigation)
    const company  = this.authStateService.currentCompany || localStorage.getItem('currentCompany') || 'unity';
    const token    = this.authStateService.getCompanyTokenData(company)?.token || '';
    const userName = this.authStateService.getUserName() || this.getUserName() || '';
    fetch(`${environment.CAS_BASE_RUL}api/CAS/signout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Client-Code': 'CRM' },
      credentials: 'include',
      body: JSON.stringify({ userName, tokens: { [company]: token } }),
      keepalive: true
    }).catch(() => {});

    if (this.isUserLoggedIn()) {
      await this.authCleanupService.handleLogout();
    }

    this.stateService.resetState();

    const keyToKeep   = 'currentCompany';
    const valueToKeep = localStorage.getItem(keyToKeep);

    // 2. Clear integrity snapshot BEFORE any localStorage mutation
    //    (must happen before setLoginStatus so validateIntegrity never sees
    //     isLoggedIn="false" while the snapshot still expects "true")
    this.storageIntegrityService.clearState();

    // 3. Now safe to flip login flags — snapshot is already gone
    this.setLoginStatus(false);

    // 3. Reset in-memory auth state
    this.authStateService.reset();
    this.purgeStoredTokens();

    localStorage.clear();
    if (valueToKeep !== null) {
      localStorage.setItem(keyToKeep, valueToKeep);
    }

    this.httpService.ngOnDestroy();
    indexedDB.deleteDatabase('PermissionsDB');

    this.router.navigateByUrl('auth/login');
    this.isCustomerLogin = false;
    this.getInactivityService()?.stop();
    sessionStorage.clear();
    this.echoService.unsubscribe('new_notification');
    this.sentryUserService.clearUserContext();
  }

  setCompanyTokens(tokens:any){
    this.unityToken= tokens?.unityToken;
    this.mingleToken= tokens?.mingleToken;
    this.unityUserID= tokens?.unityUserID;
    this.mingleUserID= tokens?.mingleUserID;
  }

  /**
   * Get current company being displayed
   */
  getCurrentCompany(): string {
    return localStorage.getItem('currentCompany') || 'unity';
  }

  async getCompanyTokens(company: string): Promise<{ token: string; userId: number }> {
    try {
      console.log(`Getting token for company: '${company}', current company in local storage: '${localStorage.getItem('currentCompany')}'`);

      const userDetailsKey = `tibss.${company}-userDetails`;

      let userDetails;
      try {
        const userDetailsJson = localStorage.getItem(userDetailsKey) || '{}';
        userDetails = JSON.parse(userDetailsJson);
        console.log(`${company} user details from storage:`, userDetails?.userID ? `Found (ID: ${userDetails.userID})` : 'Not found');
      } catch (e) {
        console.error(`Error parsing ${company} user details:`, e);
        userDetails = {};
      }

      let token = this.authStateService.getCompanyTokenData(company)?.token || null;
      if (!token) {
        try {
          await this.tokenService.refreshToken(company);
          token = this.authStateService.getCompanyTokenData(company)?.token || null;
        } catch (refreshError) {
          console.warn(`Could not rehydrate token for ${company}:`, refreshError);
        }
      }

      const userId = this.authStateService.getCompanyTokenData(company)?.userId || userDetails?.userID || null;

      if (!token || !userId) {
        console.error(`${company} token or user details missing.`);
        throw new Error(`${company} token or user details missing.`);
      }

      console.log(`Returning ${company} token: ${token.substring(0, 15)}... and userId: ${userId}`);
      return { token, userId };
    } catch (error) {
      console.error(`Error fetching tokens for ${company}:`, error.message);
      throw error;
    }
  }
  
  
  
  
  isTokenExpired(): boolean {
    const currentCompany = localStorage.getItem("currentCompany") || "unity";
    return this.tokenService.validateToken(currentCompany).isExpired;
  }

  isRefreshTokenAboutToExpire(): boolean {
    const currentCompany = localStorage.getItem("currentCompany") || "unity";
    return this.tokenService.validateToken(currentCompany).shouldRefresh;
  }

  showRefreshTokenDialog(): Promise<boolean> {
    if (this.isRefreshingToken) {
      return Promise.resolve(false); // Return immediately if already in the process
    }

    this.isRefreshTokenDialogAlreadyDisplayed = true;
    return Swal.fire({
      title: "Your Session is about to expire.",
      text: "Do you want to continue the session?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        this.generateRefreshTokenByRefereshToken();
      }
      return result.isConfirmed;
    });
  }

  showTokenExpiredDialog(): Promise<boolean> {
    this.isExpiredTokenDialogAlreadyDisplayed = true;
    return Swal.fire({
      title: "Session Expired.",
      text: "Login again.",
      icon: "error",
      confirmButtonText: "Login",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        this.storageIntegrityService.clearState();
        this.authStateService.reset();
        localStorage.clear();
        this.router.navigateByUrl("auth/login");
        console.clear();
      }
      return result.isConfirmed;
    });
  }
}
